import { inject, Injectable } from '@angular/core';
import { masterdataEntityDefinitions } from '../masterdata-entity.definitions';
import {
  MasterdataDetailPanel,
  SOFTLINE_CONFIG_MASTERDATA_DETAIL_PANELS,
  SOFTLINE_CONFIG_MASTERDATA_ENTITIES,
} from '../masterdata.providers';
import { MasterdataEntityDefinition } from '../types/masterdata-entity-definition';

/**
 * Resolves masterdata entity definitions by name. Built-ins are seeded first;
 * app-provided definitions (via `provideMasterdataEntities`) with the same
 * `name` override them. Detail panels contributed through
 * `provideMasterdataDetailPanels` are resolved by entity name as well.
 */
@Injectable({ providedIn: 'root' })
export class MasterdataEntityRegistry {
  private readonly byName = new Map<string, MasterdataEntityDefinition<unknown>>(
    [
      ...masterdataEntityDefinitions(),
      ...(inject(SOFTLINE_CONFIG_MASTERDATA_ENTITIES, { optional: true }) ?? []).flat(),
    ].map((definition) => [definition.name, definition])
  );

  private readonly detailPanels = new Map<string, MasterdataDetailPanel>(
    (inject(SOFTLINE_CONFIG_MASTERDATA_DETAIL_PANELS, { optional: true }) ?? [])
      .flat()
      .map((panel) => [panel.entity, panel])
  );

  find(name: string): MasterdataEntityDefinition<unknown> | null {
    return this.byName.get(name) ?? null;
  }

  get(name: string): MasterdataEntityDefinition<unknown> {
    const definition = this.find(name);
    if (!definition)
      throw new Error(
        `Unknown masterdata entity '${name}'. Known entities: ${[...this.byName.keys()].join(', ')}`
      );
    return definition;
  }

  /** The panel an app contributed for the entity, if any. */
  detailPanel(name: string): MasterdataDetailPanel | null {
    return this.detailPanels.get(name) ?? null;
  }

  all(): MasterdataEntityDefinition<unknown>[] {
    return [...this.byName.values()];
  }
}
